export enum ErrorCode {
  LoginInvalidCredentials = "loginInvalidCredentials",
  LoginInvalidCredentialsTotp = "loginInvalidCredentialsTotp",
  LoginInactiveUser = "LoginInactiveUser",
  PatientAccountInvalidCredentials = "PatientAccountInvalidCredentials",
  PatientAccountInvalidCredentialsMfa = "PatientAccountInvalidCredentialsMfa",
  PatientAccountEmailAlreadyInUse = "PatientAccountEmailAlreadyInUse",
  PatientAccountLoginAttemptsExceeded = "PatientAccountLoginAttemptsExceeded",
  PatientAccountPasswordResetExpired = "PatientAccountPasswordResetExpired",
  PatientAccountLoginExpired = "PatientAccountLoginExpired",
  PatientAccountSmsAttemptsExceeded = "PatientAccountSmsAttemptsExceeded",
  PatientAccountSmsIncorrectCode = "PatientAccountSmsIncorrectCode",
  PatientAccountDuplicateEmail = "PatientAccountDuplicateEmail",
  RegistrationClosed = "RegistrationClosed",
  RegistrationAlreadyCreated = "RegistrationAlreadyCreated",
  RegistrationRequestExistingPatient = "RegistrationRequestExistingPatient",
  // Staff-created registration: the matched person already has an active
  // (Undecided) registration request or an active patient in this practice.
  RegistrationRequestDuplicateActive = "RegistrationRequestDuplicateActive",
  RegistrationRequestAcceptedHisKnown = "RegistrationRequestAcceptedHisKnown",
  RegistrationRequestCompletedHisUnknown = "RegistrationRequestCompletedHisUnknown",
  // Fail-closed guard on the accept path: a passant must never produce an ION
  // aanmeldbericht, and the bypass that would allow it does not exist yet
  // (backend/src/registration/ROADMAP.md phase 1).
  RegistrationRequestPassantNotSupported = "RegistrationRequestPassantNotSupported",
  ReregistrationHisUnknown = "ReregistrationHisUnknown",
  RegistrationHisFailed = "RegistrationHisFailed",
  AppointmentCreationClosed = "AppointmentCreationClosed",
  // Expected outcome of a booking race: the slot was taken (or expired) between
  // the patient loading the slot list and submitting the booking.
  AppointmentSlotUnavailable = "AppointmentSlotUnavailable",
  WalkInConsultCreationClosed = "WalkInConsultCreationClosed",
  WalkInConsultCreationQueueFull = "WalkInConsultCreationQueueFull",
  IdentificationCheckError = "IdentificationCheckError",
  NoAgendaConfigured = "NoAgendaConfigured",
  HISLogout = "HISLogout",
  DuplicatePassword = "DuplicatePassword",
  PatientBSNAlreadyExists = "PatientBSNAlreadyExists",
  PatientBSNInvalid = "PatientBSNInvalid",
  CommunicationLogAccessDenied = "CommunicationLogAccessDenied",
  // Expected when a (time-limited) practice authorization expired or was
  // revoked while the frontend was still open — suppressed from warn-logs
  // and Sentry in `AllExceptionsFilter`.
  PracticeConfigurationAccessDenied = "PracticeConfigurationAccessDenied",
  UsernameAlreadyInUse = "UsernameAlreadyInUse",

  MfaSessionNotFound = "mfaSessionNotFound",
  MfaInvalidToken = "mfaInvalidToken",
  MfaSessionExpired = "mfaSessionExpired",
  MfaMaxAttemptsReached = "mfaMaxAttemptsReached",
  MfaContextMismatch = "mfaContextMismatch",
  MfaCallNotAllowed = "mfaCallNotAllowed",
  MfaDeliveryCooldown = "mfaDeliveryCooldown",
  MfaDeliveryFailed = "mfaDeliveryFailed",

  // E-consult public access errors
  EconsultPatientMissingPhoneNumber = "EconsultPatientMissingPhoneNumber",

  // Page invitation errors
  InvitationRecipientMismatch = "InvitationRecipientMismatch",
  InvitationNotRedeemable = "InvitationNotRedeemable",

  // User invitation errors
  DuplicateUserInvitation = "DuplicateUserInvitation",
  InvitationAccountAlreadyExists = "InvitationAccountAlreadyExists",
  EmailDomainBlocked = "EmailDomainBlocked",
  EmailNotDeliverable = "EmailNotDeliverable",

  // File import errors
  UnsupportedImportFile = "UnsupportedImportFile",

  // File upload errors
  FileContentTypeNotAllowed = "FileContentTypeNotAllowed",
  FileTooLarge = "FileTooLarge",
  ImageDimensionsTooLarge = "ImageDimensionsTooLarge",
  ImageProcessingFailed = "ImageProcessingFailed",

  // Postal code area errors
  PostalCodeP4DoesNotExist = "PostalCodeP4DoesNotExist",

  // UZI certificate upload errors (IT-4938). Surfaced inline under the file
  // pickers by UziCertificateSection, not toasted by the generic interceptor.
  UziCertificatePublicUnreadable = "UziCertificatePublicUnreadable",
  UziCertificateIssuerNotAccepted = "UziCertificateIssuerNotAccepted",
  UziCertificatePrivateUnreadable = "UziCertificatePrivateUnreadable",
  UziCertificateKeyPairMismatch = "UziCertificateKeyPairMismatch",

  // Practice Vecozo permission probes on settings save (IT-4944). Toasted by
  // the registration-settings form itself, not the generic interceptor.
  VecozoCovPermissionDenied = "VecozoCovPermissionDenied",
  VecozoIonPermissionDenied = "VecozoIonPermissionDenied",

  // A registration AGB code that is not a practice AGB code in the Vektis
  // registry (IT-4878). Toasted by the registration-settings form.
  PracticeRegistrationAgbUnknown = "PracticeRegistrationAgbUnknown",

  // Anonymous e-consult link errors (phone UI)
  EconsultLinkCallerNumberInvalid = "EconsultLinkCallerNumberInvalid",
  EconsultLinkCallerNumberNotMobile = "EconsultLinkCallerNumberNotMobile",
  EconsultLinkTemplateDisabled = "EconsultLinkTemplateDisabled",
  // The SMS provider refused/failed the send; the e-consult creation was rolled
  // back with it, so pressing the send button again retries from scratch.
  EconsultLinkSmsFailed = "EconsultLinkSmsFailed",

  // Care group dashboard errors
  // The global build lock is held (by the recurring job or another admin), so
  // the on-demand rebuild did not run. The dashboard shell toasts a "try again
  // in a few minutes" message on this code instead of the generic error.
  DashboardDatabaseBuildRunning = "DashboardDatabaseBuildRunning",
  // No snapshot exists for this care group yet — the scheduled build has not run
  // for it. Expected on the dashboard's pre-flight, which renders an empty state
  // on this code instead of opening a database (and suppresses the toast).
  DashboardDatabaseNotBuilt = "DashboardDatabaseNotBuilt",

  // Plato errors we can handle (see `PlatoErrorType`)
  NOT_CURRENT_QUESTION = "NOT_CURRENT_QUESTION",
}
